import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import InviteSection from '../../components/Invites/InviteSection';
import axios from 'axios';
import { toast } from 'react-toastify';
import './SettingsPage.css';

const API_AUTH_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SettingsPage = () => {
    const { t, i18n } = useTranslation();
    const { user, token, logout, relogin } = useAuth();
    const navigate = useNavigate();

    // UI States
    const [view, setView] = useState('default'); // 'default', 'password', 'email', 'email_verify'
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({ old: false, new: false, confirm: false });

    // Form States
    const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [emailData, setEmailData] = useState({ newEmail: '', code: '' });

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleLanguage = () => {
        const nextLang = i18n.language === 'pt' ? 'en' : 'pt';
        i18n.changeLanguage(nextLang);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return toast.error(t('passwords_dont_match'));
        }
        setLoading(true);
        try {
            await axios.post(`${API_AUTH_URL}/change-password`, {
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(t('password_change_success'));
            setView('default');
            setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            toast.error(err.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleInitiateEmailChange = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API_AUTH_URL}/change-email/init`, {
                newEmail: emailData.newEmail
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.info(t('verify_new_email_desc'));
            setView('email_verify');
        } catch (err) {
            toast.error(err.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyEmailChange = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API_AUTH_URL}/change-email/verify`, {
                code: emailData.code
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(t('email_change_success'));
            await relogin(); // Update local user data
            setView('default');
            setEmailData({ newEmail: '', code: '' });
        } catch (err) {
            toast.error(err.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings-container">
            <header className="settings-header">
                {view === 'default' ? (
                    <>
                        <h1>{t('settings', 'Configurações')}</h1>
                        <p>{t('settings_desc', 'Gerencie sua conta e preferências.')}</p>
                    </>
                ) : (
                    <div className="back-header">
                        <button className="back-btn-ui" onClick={() => setView('default')}>← {t('back', 'Voltar')}</button>
                    </div>
                )}
            </header>

            <div className="settings-grid">
                {view === 'default' && (
                    <>
                        {/* Account Section */}
                        <section className="settings-group">
                            <h2 className="group-title">
                                <span className="icon">👤</span> {t('profile_settings', 'Conta')}
                            </h2>
                            <div className="settings-cards">
                                <div className="settings-card" onClick={() => navigate('/profile/edit')}>
                                    <div className="card-content">
                                        <h3>{t('edit_profile', 'Editar Perfil')}</h3>
                                        <p>{t('edit_profile_desc', 'Mude seu nome, bio e fotos.')}</p>
                                    </div>
                                    <span className="card-arrow">→</span>
                                </div>

                                <div className="settings-card no-click">
                                    <div className="card-content">
                                        <h3>{t('account_email', 'E-mail da Conta')}</h3>
                                        <p>{user?.email}</p>
                                    </div>
                                </div>

                                <div className="settings-card" onClick={toggleLanguage}>
                                    <div className="card-content">
                                        <h3>{t('language', 'Idioma')}</h3>
                                        <p>{i18n.language === 'pt' ? t('portuguese', 'Português') : t('english', 'Inglês')}</p>
                                    </div>
                                    <span className="card-arrow">→</span>
                                </div>
                            </div>
                        </section>

                        {/* Security Section */}
                        <section className="settings-group">
                            <h2 className="group-title">
                                <span className="icon">🔒</span> {t('security', 'Segurança')}
                            </h2>
                            <div className="settings-cards">
                                <div className="settings-card" onClick={() => setView('password')}>
                                    <div className="card-content">
                                        <h3>{t('change_password', 'Alterar Senha')}</h3>
                                        <p>{t('change_password_desc', 'Mantenha sua conta segura.')}</p>
                                    </div>
                                    <span className="card-arrow">→</span>
                                </div>

                                <div className="settings-card" onClick={() => setView('email')}>
                                    <div className="card-content">
                                        <h3>{t('change_email', 'Alterar E-mail')}</h3>
                                        <p>{t('change_email_desc_short', 'Trocar e-mail de acesso à conta.')}</p>
                                    </div>
                                    <span className="card-arrow">→</span>
                                </div>
                            </div>
                        </section>

                        {/* Professional Section */}
                        {user?.role === 'client' && (
                            <section className="settings-group">
                                <h2 className="group-title">
                                    <span className="icon">🎨</span> {t('professional', 'Profissional')}
                                </h2>
                                <div className="settings-cards">
                                    <div className="settings-card card-pro-highlight" onClick={() => navigate('/become-artist')}>
                                        <div className="card-content">
                                            <h3>🚀 {t('become_artist', 'Sou Tatuador')}</h3>
                                            <p>{t('become_artist_settings_desc', 'Inicie seu processo de verificação para publicar artes.')}</p>
                                        </div>
                                        <span className="card-arrow">→</span>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* Legal Section */}
                        <section className="settings-group">
                            <h2 className="group-title">
                                <span className="icon">⚖️</span> {t('about_legal', 'Sobre e Legal')}
                            </h2>
                            <div className="settings-cards">
                                <div className="settings-card" onClick={() => navigate('/terms')}>
                                    <div className="card-content">
                                        <h3>{t('terms_of_service_title', 'Termos de Uso')}</h3>
                                        <p>{t('view_terms_desc', 'Leia nossos termos de serviço.')}</p>
                                    </div>
                                    <span className="card-arrow">→</span>
                                </div>

                                <div className="settings-card" onClick={() => navigate('/privacy')}>
                                    <div className="card-content">
                                        <h3>{t('privacy_policy_title', 'Política de Privacidade')}</h3>
                                        <p>{t('view_privacy_desc', 'Saiba como tratamos seus dados.')}</p>
                                    </div>
                                    <span className="card-arrow">→</span>
                                </div>
                            </div>
                        </section>

                        {/* Invites for Artists/Studios */}
                        {(user?.role === 'artist' || user?.role === 'studio') && (
                            <section className="settings-group full-width">
                                <InviteSection />
                            </section>
                        )}
                    </>
                )}

                {view === 'password' && (
                    <section className="settings-form-section">
                        <h2>{t('change_password')}</h2>
                        <form onSubmit={handleChangePassword}>
                            <div className="form-group-ui">
                                <label>{t('current_password')}</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPasswords.old ? "text" : "password"}
                                        value={passwordData.oldPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => togglePasswordVisibility('old')}
                                    >
                                        {showPasswords.old ? (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group-ui">
                                <label>{t('new_password')}</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPasswords.new ? "text" : "password"}
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => togglePasswordVisibility('new')}
                                    >
                                        {showPasswords.new ? (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group-ui">
                                <label>{t('confirm_password')}</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPasswords.confirm ? "text" : "password"}
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => togglePasswordVisibility('confirm')}
                                    >
                                        {showPasswords.confirm ? (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <button type="submit" className="save-btn-ui" disabled={loading}>
                                {loading ? t('loading') : t('save_changes', 'Salvar Alterações')}
                            </button>
                        </form>
                    </section>
                )}

                {view === 'email' && (
                    <section className="settings-form-section">
                        <h2>{t('change_email')}</h2>
                        <form onSubmit={handleInitiateEmailChange}>
                            <div className="form-group-ui">
                                <label>{t('new_email')}</label>
                                <input
                                    type="email"
                                    value={emailData.newEmail}
                                    onChange={(e) => setEmailData({ ...emailData, newEmail: e.target.value })}
                                    placeholder="novo@email.com"
                                    required
                                />
                            </div>
                            <button type="submit" className="save-btn-ui" disabled={loading}>
                                {loading ? t('loading') : t('send_code')}
                            </button>
                        </form>
                    </section>
                )}

                {view === 'email_verify' && (
                    <section className="settings-form-section">
                        <h2>{t('verify_new_email')}</h2>
                        <p className="verify-hint">{t('verify_new_email_desc')}</p>
                        <form onSubmit={handleVerifyEmailChange}>
                            <div className="form-group-ui">
                                <label>{t('verification_code')}</label>
                                <input
                                    type="text"
                                    value={emailData.code}
                                    onChange={(e) => setEmailData({ ...emailData, code: e.target.value })}
                                    placeholder="000000"
                                    maxLength="6"
                                    required
                                />
                            </div>
                            <button type="submit" className="save-btn-ui" disabled={loading}>
                                {loading ? t('loading') : t('verify_button')}
                            </button>
                        </form>
                    </section>
                )}
            </div>

            {view === 'default' && (
                <footer className="settings-footer">
                    <button className="logout-btn" onClick={handleLogout}>
                        {t('logout', 'Sair da Conta')}
                    </button>
                </footer>
            )}
        </div>
    );
};

export default SettingsPage;
